import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceSelector from './components/ServiceSelector';
import LocationInput from './components/LocationInput';
import VehiclePicker, { vehicles } from './components/VehiclePicker';
import FareEstimate from './components/FareEstimate';
import ScheduleToggle from './components/ScheduleToggle';
import BookingConfirmation from './components/BookingConfirmation';
import OrderHistorySection from './components/OrderHistorySection';
import FavoriteRoutes from './components/FavoriteRoutes';
import DeliveryDetailsForm from './components/DeliveryDetailsForm';
import { orderService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import { NotificationBell } from '../../components/NotificationPanel';
import NotificationPreferencesDrawer from '../../components/NotificationPreferencesDrawer';
import SpinnerOverlay from '../../components/ui/SpinnerOverlay';
import SuccessToast from '../../components/ui/SuccessToast';

const CustomerBookingScreen = () => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState('ride');
  const [pickup, setPickup] = useState('');
  const [dropoff, setDropoff] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState('boda');
  const [scheduleType, setScheduleType] = useState('now');
  const [scheduledTime, setScheduledTime] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [distance] = useState(3.2);
  const [step, setStep] = useState(1);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingError, setBookingError] = useState(null);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [isGpsDetecting, setIsGpsDetecting] = useState(false);
  const [driverRatings, setDriverRatings] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [showNotifPrefs, setShowNotifPrefs] = useState(false);
  const [bookingToast, setBookingToast] = useState(false);

  // Delivery-specific state
  const [deliveryDetails, setDeliveryDetails] = useState({
    senderName: '',
    senderPhone: '',
    packageType: 'parcel',
    weight: '',
    notes: '',
    recipientName: '',
    recipientPhone: '',
  });

  const isDelivery = selectedService === 'delivery';

  // For delivery, auto-select bike/boda
  useEffect(() => {
    if (isDelivery && !['bike', 'boda']?.includes(selectedVehicle)) {
      setSelectedVehicle('boda');
    }
  }, [isDelivery]);

  const canProceedToVehicle = pickup && dropoff && pickup !== 'Locating you...';
  const deliveryFormValid = !isDelivery || (
    deliveryDetails?.recipientName?.trim() && deliveryDetails?.recipientPhone?.trim()
  );
  const canBook = pickup && dropoff && selectedVehicle && pickup !== 'Locating you...' && deliveryFormValid;

  const getVehicleFare = () => {
    const vehicleRates = {
      bike: { base: 50, perKm: 15 },
      boda: { base: 80, perKm: 20 },
      tuktuk: { base: 120, perKm: 25 },
      car: { base: 200, perKm: 35 }
    };
    const rate = vehicleRates?.[selectedVehicle] || vehicleRates?.boda;
    const base = rate?.base + Math.round(rate?.perKm * distance);
    const fee = Math.round(base * 0.05);
    return base + fee;
  };

  const handleBookNow = async () => {
    if (!canBook) return;
    setBookingLoading(true);
    setBookingError(null);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (user) {
        const fare = getVehicleFare();
        const platformFee = Math.round(fare * 0.05);

        // Build delivery_details JSONB for delivery orders
        const deliveryPayload = isDelivery ? {
          sender_name: deliveryDetails?.senderName,
          sender_phone: deliveryDetails?.senderPhone,
          package_type: deliveryDetails?.packageType,
          weight: deliveryDetails?.weight ? parseFloat(deliveryDetails?.weight) : null,
          notes: deliveryDetails?.notes,
          recipient_name: deliveryDetails?.recipientName,
          recipient_phone: deliveryDetails?.recipientPhone,
        } : null;

        const order = await orderService?.createOrder(user?.id, {
          serviceType: selectedService,
          vehicleType: isDelivery ? 'boda' : selectedVehicle,
          pickupAddress: pickup,
          dropoffAddress: dropoff,
          distanceKm: distance,
          fareAmount: fare - platformFee,
          platformFee,
          totalAmount: fare,
          scheduledAt: scheduleType === 'later' && scheduledTime ? new Date(scheduledTime)?.toISOString() : null,
          deliveryDetails: deliveryPayload,
        });
        setCreatedOrder(order);

        // Trigger broadcast matching engine for immediate bookings
        if (scheduleType === 'now' && order?.id) {
          try {
            await supabase?.rpc('broadcast_order_to_drivers', {
              p_order_id: order?.id,
              p_round: 1,
            });
          } catch (broadcastErr) {
            // Non-fatal: order created, broadcast may retry
            console.warn('Broadcast trigger failed:', broadcastErr);
          }
        }
      }
    } catch (err) {
      console.error('Booking error:', err);
      setBookingError('Failed to create booking. Please try again.');
    } finally {
      setBookingLoading(false);
      setShowConfirmation(true);
    }
  };

  const handleNewBooking = () => {
    setShowConfirmation(false);
    setPickup('');
    setDropoff('');
    setSelectedVehicle('boda');
    setScheduleType('now');
    setScheduledTime('');
    setStep(1);
    setCreatedOrder(null);
    setBookingError(null);
    setDeliveryDetails({
      senderName: '', senderPhone: '', packageType: 'parcel',
      weight: '', notes: '', recipientName: '', recipientPhone: '',
    });
  };

  const handleRebook = ({ pickup: p, dropoff: d, vehicle, service, payment }) => {
    setPickup(p || '');
    setDropoff(d || '');
    setSelectedVehicle(vehicle || 'boda');
    setSelectedService(service || 'ride');
    setPaymentMethod(payment || 'cash');
    if (payment) {
      localStorage.setItem('otg_last_payment_method', payment);
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Auto-detect GPS on mount
  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 900);
    return () => clearTimeout(timer);
  }, []);

  // Fetch driver ratings per vehicle type from order_ratings
  useEffect(() => {
    const fetchDriverRatings = async () => {
      try {
        const { data, error } = await supabase?.from('order_ratings')?.select(`
            rating,
            reviewer_role,
            orders!order_ratings_order_id_fkey (
              vehicle_type,
              driver_id
            )
          `)?.eq('reviewer_role', 'customer');

        if (error) throw error;

        const ratingsByVehicle = {};
        (data || [])?.forEach((row) => {
          const vehicleType = row?.orders?.vehicle_type;
          if (!vehicleType) return;
          if (!ratingsByVehicle?.[vehicleType]) {
            ratingsByVehicle[vehicleType] = { total: 0, count: 0 };
          }
          ratingsByVehicle[vehicleType].total += row?.rating;
          ratingsByVehicle[vehicleType].count += 1;
        });

        const computed = {};
        Object.keys(ratingsByVehicle)?.forEach((vt) => {
          const { total, count } = ratingsByVehicle?.[vt];
          computed[vt] = {
            avgRating: count > 0 ? (total / count)?.toFixed(1) : null,
            reviewCount: count,
          };
        });
        setDriverRatings(computed);
      } catch (err) {
        console.error('Failed to fetch driver ratings:', err);
      }
    };
    fetchDriverRatings();
  }, []);

  // Trigger GPS auto-detect after page loads
  useEffect(() => {
    if (!pageLoading && !pickup) {
      if (navigator?.geolocation) {
        setIsGpsDetecting(true);
        setPickup('Locating you...');

        const reverseGeocode = async (lat, lng) => {
          try {
            const res = await fetch(
              `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&addressdetails=1`,
              { headers: { 'Accept-Language': 'en' } }
            );
            const data = await res?.json();
            if (data?.display_name) {
              const addr = data?.address;
              const parts = [
                addr?.road || addr?.pedestrian || addr?.footway,
                addr?.suburb || addr?.neighbourhood || addr?.quarter,
                addr?.city || addr?.town || addr?.village,
              ]?.filter(Boolean);
              return parts?.length > 0 ? parts?.join(', ') : data?.display_name?.split(',')?.slice(0, 3)?.join(',')?.trim();
            }
          } catch {
            // ignore
          }
          return `${lat?.toFixed(5)}, ${lng?.toFixed(5)}`;
        };

        navigator.geolocation?.getCurrentPosition(
          async (position) => {
            const { latitude, longitude } = position?.coords;
            const address = await reverseGeocode(latitude, longitude);
            setPickup(address);
            setIsGpsDetecting(false);
          },
          () => {
            setPickup('');
            setIsGpsDetecting(false);
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
        );
      }
    }
  }, [pageLoading]);

  // Auto-advance step when locations are filled
  useEffect(() => {
    if (pickup && dropoff && pickup !== 'Locating you...' && step === 1) {
      setStep(2);
    }
  }, [pickup, dropoff]);

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f2040] to-[#1a365d] relative overflow-hidden">
        {/* Background decorative */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ffd700]/5 blur-3xl" />
          <div className="absolute top-1/2 -left-24 w-64 h-64 rounded-full bg-[#ffd700]/3 blur-2xl" />
        </div>
        {/* Skeleton Header */}
        <div className="relative z-10 px-4 pt-6 pb-4">
          <div className="max-w-lg mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-[#ffd700]/30 animate-pulse" style={{ animationDelay: '0ms' }} />
                  <div className="h-4 w-20 rounded-full bg-[#ffd700]/20 animate-pulse" style={{ animationDelay: '60ms' }} />
                </div>
                <div className="h-6 w-28 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: '120ms' }} />
                <div className="h-3 w-44 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: '180ms' }} />
              </div>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-white/10 animate-pulse" style={{ animationDelay: '240ms' }} />
                <div className="w-10 h-10 rounded-xl bg-white/10 animate-pulse" style={{ animationDelay: '300ms' }} />
              </div>
            </div>

            {/* Map Preview Skeleton */}
            <div className="relative h-36 rounded-2xl overflow-hidden mb-4 bg-[#0a1628]/40 border border-white/10 animate-pulse" style={{ animationDelay: '200ms' }}>
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 400 150" preserveAspectRatio="xMidYMid slice">
                {[0, 30, 60, 90, 120, 150]?.map(y => (
                  <line key={`h${y}`} x1="0" y1={y} x2="400" y2={y} stroke="#ffd700" strokeWidth="0.5" />
                ))}
                {[0, 50, 100, 150, 200, 250, 300, 350, 400]?.map(x => (
                  <line key={`v${x}`} x1={x} y1="0" x2={x} y2="150" stroke="#ffd700" strokeWidth="0.5" />
                ))}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: '350ms' }} />
              </div>
            </div>
          </div>
        </div>
        {/* Skeleton Form Card */}
        <div className="relative z-10 bg-[#f8fafc] rounded-t-[2rem] min-h-screen px-4 pt-6 pb-32">
          <div className="max-w-lg mx-auto space-y-5">

            {/* Service Selector Skeleton */}
            <div className="bg-white rounded-2xl p-4 border border-[#0a1628]/8 shadow-sm">
              <div className="h-3 w-24 rounded-full bg-[#0a1628]/10 animate-pulse mb-3" style={{ animationDelay: '200ms' }} />
              <div className="grid grid-cols-2 gap-2">
                {[250, 310]?.map(delay => (
                  <div key={delay} className="h-16 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: `${delay}ms` }} />
                ))}
              </div>
            </div>

            {/* Location Input Skeleton */}
            <div className="bg-white rounded-2xl p-4 border border-[#0a1628]/8 shadow-sm space-y-3">
              <div className="h-3 w-20 rounded-full bg-[#0a1628]/10 animate-pulse" style={{ animationDelay: '300ms' }} />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#ffd700]/20 animate-pulse flex-shrink-0" style={{ animationDelay: '360ms' }} />
                <div className="flex-1 h-11 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: '420ms' }} />
              </div>
              <div className="w-px h-4 bg-[#0a1628]/10 mx-4" />
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#0a1628]/10 animate-pulse flex-shrink-0" style={{ animationDelay: '480ms' }} />
                <div className="flex-1 h-11 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: '540ms' }} />
              </div>
            </div>

            {/* Vehicle Picker Skeleton */}
            <div className="bg-white rounded-2xl p-4 border border-[#0a1628]/8 shadow-sm">
              <div className="h-3 w-28 rounded-full bg-[#0a1628]/10 animate-pulse mb-3" style={{ animationDelay: '380ms' }} />
              <div className="grid grid-cols-2 gap-3">
                {[430, 490, 550, 610]?.map(delay => (
                  <div key={delay} className="h-20 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: `${delay}ms` }} />
                ))}
              </div>
            </div>

            {/* Fare Estimate Skeleton */}
            <div className="bg-white rounded-2xl p-4 border border-[#0a1628]/8 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <div className="h-3 w-24 rounded-full bg-[#0a1628]/10 animate-pulse" style={{ animationDelay: '460ms' }} />
                <div className="h-5 w-20 rounded-full bg-[#ffd700]/20 animate-pulse" style={{ animationDelay: '520ms' }} />
              </div>
              <div className="space-y-2">
                {[510, 570, 630]?.map(delay => (
                  <div key={delay} className="flex justify-between">
                    <div className="h-2.5 w-20 rounded-full bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: `${delay}ms` }} />
                    <div className="h-2.5 w-16 rounded-full bg-[#0a1628]/10 animate-pulse" style={{ animationDelay: `${delay + 30}ms` }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Schedule Toggle Skeleton */}
            <div className="bg-white rounded-2xl p-4 border border-[#0a1628]/8 shadow-sm">
              <div className="h-3 w-20 rounded-full bg-[#0a1628]/10 animate-pulse mb-3" style={{ animationDelay: '560ms' }} />
              <div className="grid grid-cols-2 gap-2">
                <div className="h-10 rounded-xl bg-[#0a1628]/8 animate-pulse" style={{ animationDelay: '610ms' }} />
                <div className="h-10 rounded-xl bg-[#0a1628]/6 animate-pulse" style={{ animationDelay: '670ms' }} />
              </div>
            </div>

          </div>
        </div>
        {/* Skeleton Book Button */}
        <div className="fixed bottom-0 left-0 right-0 z-20 px-4 pb-6 pt-3 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/95 to-transparent">
          <div className="max-w-lg mx-auto">
            <div className="w-full h-14 rounded-2xl bg-[#0a1628]/15 animate-pulse" style={{ animationDelay: '700ms' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0f2040] to-[#1a365d] relative overflow-hidden">
      <SpinnerOverlay
        visible={bookingLoading}
        title={isDelivery ? 'Creating delivery...' : 'Creating your booking...'}
        message={isDelivery ? 'Finding nearby delivery riders' : 'Connecting you with nearby drivers'}
      />
      <SuccessToast
        visible={bookingToast}
        message={isDelivery ? 'Delivery request sent!' : 'Booking request sent!'}
        subMessage="Finding the nearest driver for you"
        onDismiss={() => setBookingToast(false)}
        duration={3500}
        position="top"
      />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#ffd700]/5 blur-3xl"></div>
        <div className="absolute top-1/2 -left-24 w-64 h-64 rounded-full bg-[#ffd700]/3 blur-2xl"></div>
        <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-[#1a365d]/50 blur-3xl"></div>
      </div>
      {/* Header */}
      <div className="relative z-10 px-4 pt-safe-top pt-6 pb-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-lg bg-[#ffd700] flex items-center justify-center">
                  <svg viewBox="0 0 16 16" fill="none" className="w-3.5 h-3.5">
                    <path d="M8 1l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z" fill="#0a1628" />
                  </svg>
                </div>
                <span className="text-[#ffd700] font-bold text-lg tracking-tight">OnTheGo</span>
              </div>
              <h1 className="heading-1 text-white">{isDelivery ? 'Send a Package' : 'Book a Trip'}</h1>
              <p className="body-text text-white/50 mt-0.5">{isDelivery ? 'Fast, reliable delivery across Kenya' : 'Fast, reliable rides across Kenya'}</p>
            </div>
            <div className="flex items-center gap-2">
              {/* GPS detecting indicator in header */}
              {isGpsDetecting && (
                <div className="flex items-center gap-1.5 bg-green-500/15 border border-green-500/30 rounded-full px-3 py-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-green-400 text-xs font-semibold">Locating you...</span>
                </div>
              )}
              {/* Notification Bell */}
              <NotificationBell
                iconColor="rgba(255,255,255,0.8)"
                badgeColor="bg-red-500"
              />
              {/* Notification Preferences gear */}
              <button
                onClick={() => setShowNotifPrefs(true)}
                className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors"
                title="Notification Preferences"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.7)" strokeWidth="2" className="w-4 h-4">
                  <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                </svg>
              </button>
              <button
                onClick={() => navigate('/customer-profile-screen')}
                className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center hover:bg-white/15 transition-colors"
                title="My Profile"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-white">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Map Preview */}
          <div className="relative h-36 rounded-2xl overflow-hidden mb-4 bg-[#0a1628]/40 border border-white/10">
            <div className="absolute inset-0 bg-gradient-to-br from-[#1a365d]/80 to-[#0a1628]/60">
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 400 150" preserveAspectRatio="xMidYMid slice">
                {[0, 30, 60, 90, 120, 150]?.map((y) => (
                  <line key={`h${y}`} x1="0" y1={y} x2="400" y2={y} stroke="#ffd700" strokeWidth="0.5" />
                ))}
                {[0, 50, 100, 150, 200, 250, 300, 350, 400]?.map((x) => (
                  <line key={`v${x}`} x1={x} y1="0" x2={x} y2="150" stroke="#ffd700" strokeWidth="0.5" />
                ))}
                <path d="M50 120 Q100 80 150 90 Q200 100 250 60 Q300 30 350 40" stroke="#ffd700" strokeWidth="2" fill="none" opacity="0.6" />
                <path d="M30 60 Q80 40 130 50 Q180 60 230 30" stroke="#ffd700" strokeWidth="1.5" fill="none" opacity="0.4" />
              </svg>
            </div>
            {pickup && (
              <div className="absolute top-1/2 left-1/3 -translate-x-1/2 -translate-y-full">
                <div className="w-6 h-6 rounded-full bg-[#ffd700] border-2 border-white shadow-lg flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-[#0a1628]"></div>
                </div>
                <div className="w-0.5 h-3 bg-[#ffd700] mx-auto"></div>
              </div>
            )}
            {dropoff && (
              <div className="absolute top-1/3 right-1/3 -translate-x-1/2 -translate-y-full">
                <div className="w-6 h-6 rounded-sm bg-[#0a1628] border-2 border-white shadow-lg rotate-45 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-sm bg-white -rotate-45"></div>
                </div>
                <div className="w-0.5 h-3 bg-[#0a1628] mx-auto"></div>
              </div>
            )}
            {!pickup && !dropoff && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-8 h-8 text-white/40 mx-auto mb-1">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <p className="text-white/40 text-xs">{isDelivery ? 'Enter pickup & delivery address' : 'Enter locations to see route'}</p>
                </div>
              </div>
            )}
            {pickup && dropoff && (
              <div className="absolute bottom-2 right-2 bg-[#0a1628]/80 backdrop-blur-sm rounded-lg px-2.5 py-1.5">
                <p className="text-xs text-[#ffd700] font-medium">{distance} km · ~{Math.round(distance * 3)} min</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Booking Form Card */}
      <div className="relative z-10 bg-[#f8fafc] rounded-t-[2rem] min-h-screen px-4 pt-6 pb-32">
        <div className="max-w-lg mx-auto space-y-5">
          {/* Service Selector */}
          <ServiceSelector
            selectedService={selectedService}
            onServiceChange={(s) => {
              setSelectedService(s);
              if (s === 'delivery') setSelectedVehicle('boda');
            }}
          />

          {/* Favorite Routes */}
          <FavoriteRoutes
            onRebook={handleRebook}
            currentPickup={pickup}
            currentDropoff={dropoff}
            currentVehicle={selectedVehicle}
            currentService={selectedService}
            currentPayment={paymentMethod}
          />

          {/* Location Input */}
          <LocationInput
            pickup={pickup}
            dropoff={dropoff}
            onPickupChange={setPickup}
            onDropoffChange={setDropoff}
            service={selectedService}
            onGpsDetecting={setIsGpsDetecting}
          />

          {/* Delivery Details Form — shown before vehicle picker for delivery */}
          {isDelivery && canProceedToVehicle && (
            <div className="animate-fade-in">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-5 rounded-full bg-[#ffd700]" />
                <h3 className="text-sm font-bold text-[#0a1628]/60 uppercase tracking-wider">Delivery Details</h3>
              </div>
              <DeliveryDetailsForm
                deliveryDetails={deliveryDetails}
                onChange={setDeliveryDetails}
              />
            </div>
          )}

          {/* Vehicle Picker — for delivery only show bike/boda */}
          <div className={`transition-all duration-500 ${canProceedToVehicle || step >= 2 ? 'opacity-100 translate-y-0' : 'opacity-40 pointer-events-none'}`}>
            <VehiclePicker
              selectedVehicle={selectedVehicle}
              onVehicleChange={setSelectedVehicle}
              distance={distance}
              driverRatings={driverRatings}
              filterTypes={isDelivery ? ['bike', 'boda'] : undefined}
            />
          </div>

          {/* Fare Estimate */}
          {canProceedToVehicle && (
            <div className="animate-fade-in">
              <FareEstimate
                vehicle={selectedVehicle}
                distance={distance}
                service={selectedService}
              />
            </div>
          )}

          {/* Schedule Toggle */}
          <div className={`transition-all duration-500 ${canProceedToVehicle ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
            <ScheduleToggle
              scheduleType={scheduleType}
              onScheduleChange={setScheduleType}
              scheduledTime={scheduledTime}
              onTimeChange={setScheduledTime}
            />
          </div>

          {/* Promo Code */}
          {canProceedToVehicle && (
            <div className="flex gap-2 animate-fade-in">
              <input
                type="text"
                placeholder="Promo code"
                className="flex-1 text-sm px-4 py-3 bg-white border border-[#0a1628]/10 rounded-xl focus:outline-none focus:border-[#ffd700] focus:ring-1 focus:ring-[#ffd700]/30 transition-colors text-[#0a1628] placeholder-[#0a1628]/40"
              />
              <button className="btn-dark px-4">Apply</button>
            </div>
          )}

          {/* Delivery validation hint */}
          {isDelivery && canProceedToVehicle && !deliveryFormValid && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-700 flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 flex-shrink-0">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              Please fill in recipient name and phone to continue
            </div>
          )}

          {bookingError && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-600">
              {bookingError}
            </div>
          )}

          {/* Order History */}
          <OrderHistorySection />
        </div>
      </div>

      {/* Sticky Book Button */}
      <div className="fixed bottom-0 left-0 right-0 z-20 px-4 pb-6 pt-3 bg-gradient-to-t from-[#f8fafc] via-[#f8fafc]/95 to-transparent">
        <div className="max-w-lg mx-auto">
          <button
            onClick={handleBookNow}
            disabled={!canBook || bookingLoading}
            className={`btn-primary-full text-base ${
              canBook && !bookingLoading ? '' : 'opacity-40 cursor-not-allowed pointer-events-none'
            }`}
          >
            {bookingLoading
              ? (isDelivery ? 'Creating delivery...' : 'Creating booking...')
              : !pickup || !dropoff
              ? (isDelivery ? 'Enter pickup & delivery address' : 'Enter pickup & drop-off locations')
              : !selectedVehicle
              ? 'Select a vehicle'
              : isDelivery && !deliveryFormValid
              ? 'Fill in recipient details'
              : scheduleType === 'later'&& !scheduledTime ?'Select schedule time'
              : `${isDelivery ? '📦 Send Package' : 'Book Ride'} · KSh ${getVehicleFare()}`
            }
          </button>
          {canBook && !bookingLoading && (
            <p className="text-center text-xs text-[#0a1628]/40 mt-2">
              {isDelivery
                ? '🟢 Delivery riders available · Avg. 5 min pickup'
                : scheduleType === 'now' ?'🟢 Drivers available · Avg. 3 min pickup' :'📅 Scheduled booking'
              }
            </p>
          )}
        </div>
      </div>

      {/* Booking Confirmation Modal */}
      {showConfirmation && (
        <BookingConfirmation
          bookingData={{
            pickup,
            dropoff,
            vehicle: selectedVehicle,
            fare: getVehicleFare(),
            service: selectedService,
            scheduleType,
            scheduledTime,
            orderNumber: createdOrder?.orderNumber,
            deliveryDetails: isDelivery ? deliveryDetails : null,
          }}
          initialPaymentMethod={paymentMethod}
          onClose={() => setShowConfirmation(false)}
          onNewBooking={handleNewBooking}
        />
      )}
      <NotificationPreferencesDrawer
        isOpen={showNotifPrefs}
        onClose={() => setShowNotifPrefs(false)}
      />
    </div>
  );
};

export default CustomerBookingScreen;
